export const allowedImageMimeTypes = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];
// Extensions, not just MIME types: many browsers (Chrome/Firefox on Windows
// and Android in particular) leave file.type empty for HEIC/HEIF, since they
// have no built-in decoder for it, so MIME-type-only checks silently fail
// for the exact format we most need to detect (default iPhone camera output).
export const allowedImageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
export const maxImageSizeInBytes = 10 * 1024 * 1024;

export const validateImageFile = file => {
  if (!file) {
    return 'missing_file';
  }

  const extension = (file.name?.match(/\.[^.]+$/)?.[0] || '').toLowerCase();
  const isAllowed = file.type
    ? allowedImageMimeTypes.includes(file.type)
    : allowedImageExtensions.includes(extension);

  if (!isAllowed) {
    return 'invalid_type';
  }

  if (file.size > maxImageSizeInBytes) {
    return 'file_too_large';
  }

  return null;
};

// Extensions a browser can't decode locally in an <img>, even though they're
// accepted for upload (see allowedImageMimeTypes above).
const nonBrowserRenderableExtensions = ['.heic', '.heif'];

/**
 * Given a Cloudinary secure_url, returns a URL that a browser can actually
 * render for formats it can't decode itself (HEIC/HEIF). Cloudinary decodes
 * the source server-side regardless of the requested output format, so
 * inserting an explicit `f_jpg` transformation right after `/upload/` in the
 * URL gets back a JPEG delivery of the same asset -- no re-upload or
 * client-side conversion needed. URLs that aren't HEIC/HEIF, or that aren't
 * Cloudinary URLs at all (no `/upload/` segment), pass through unchanged.
 */
export const getPreviewableCloudinaryUrl = (url, filename) => {
  if (!url) {
    return url;
  }

  const extension = (
    url.match(/\.[^./?#]+(?=$|[?#])/)?.[0] ||
    filename?.match(/\.[^.]+$/)?.[0] ||
    ''
  ).toLowerCase();

  if (!nonBrowserRenderableExtensions.includes(extension) || !url.includes('/upload/')) {
    return url;
  }

  return url.replace('/upload/', '/upload/f_jpg,q_auto/');
};

export const uploadUnsignedImageToCloudinary = (file, options = {}) => {
  const uploadURL = process.env.REACT_APP_CLOUDINARY_URL;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_PRESET;
  const apiKey = process.env.REACT_APP_CLOUDINARY_API_KEY;

  if (!uploadURL || !uploadPreset || !apiKey) {
    return Promise.reject(new Error('Cloudinary env vars are missing'));
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', uploadPreset);
  formData.append('api_key', apiKey);
  if (options.folder) {
    formData.append('folder', options.folder);
  }

  return fetch(uploadURL, { method: 'POST', body: formData })
    .then(async response => {
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error?.message || 'Cloudinary upload failed');
      }

      return {
        secureUrl: data.secure_url,
        publicId: data.public_id,
      };
    });
};
